import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import {
  ChecklistCategoryAPIResponse,
  SiteRFPDocumentSummary,
  SiteRFPDocumentSummariesResponseRecord,
} from '@black-bear-energy/black-bear-energy-common'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { transformSiteRFPDocumentSummary } from './helpers/transformSiteRFPDocumentSummary'

/**
 * Fetch a site RFP document summary.
 * @param siteRFPId - The ID of the site RFP.
 * @param category - The checklist category to filter by.
 * @returns A UseQueryResult object containing the site RFP document summary.
 */
export const useSiteRFPDocumentSummary = (
  siteRFPId: number,
  category?: ChecklistCategoryAPIResponse
): UseQueryResult<SiteRFPDocumentSummary, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: ['siteRFPDocumentSummary', api, siteRFPId, category],
    queryFn: () =>
      fetchSiteRFPDocumentSummary(api as AxiosInstance, siteRFPId, category),
    select: transformSiteRFPDocumentSummary,
    enabled: !!api,
  })
}

async function fetchSiteRFPDocumentSummary(
  api: AxiosInstance,
  siteRFPId: number,
  category?: ChecklistCategoryAPIResponse
): Promise<SiteRFPDocumentSummariesResponseRecord> {
  const response = await api.get<SiteRFPDocumentSummariesResponseRecord>(
    `/site-rfps/${siteRFPId}/site-rfp-document-summary`,
    { params: category ? { checklistCategory: category } : undefined }
  )
  return response.data
}
